import { GridRowId } from '@mui/x-data-grid';
import { difference, intersection, isEmpty, map } from 'es-toolkit/compat';

import { SelectionMode } from 'components/atoms/RowSelectionContext';
import { ResultsMode } from 'interfaces/experimentResults';
import { getInnerRowIdFromUnwoundRowMetadata } from 'interfaces/genericFields/unwindRowsWithInnerArrays';
import { CasesParams } from './types';

export interface DatasetScopeQueryParams {
  filters?: any;
  features?: any;
  clinicalData?: any;
  searchTerm?: string;
  slidesMode?: boolean;
  resultsMode?: ResultsMode;
  caseIdsToInclude?: (string | number)[];
  slideIdsToInclude?: string[];
}

export interface DatasetScopeOverrides {
  filters?: any;
  features?: any;
  clinicalData?: any;
  slidesMode?: boolean;
  resultsMode?: ResultsMode;
}

export interface DatasetScopeInput {
  labId?: string;
  selectionMode?: SelectionMode;
  selectedRows?: GridRowId[];
  omittedRows?: GridRowId[];
  queryParams: DatasetScopeQueryParams;
  overrides?: DatasetScopeOverrides;
}

/**
 * Derives the `datasetSelection` action scope from grid selection plus encoded filters.
 * Kept hook-free so the same derivation can be tested exhaustively and reused by the action scope resolver.
 */
export const buildDatasetScopeCasesParams = ({
  labId,
  selectionMode,
  selectedRows,
  omittedRows,
  queryParams,
  overrides,
}: DatasetScopeInput): CasesParams => {
  const {
    filters,
    features,
    clinicalData,
    searchTerm,
    slidesMode,
    resultsMode,
    caseIdsToInclude: caseIdsToIncludeFilter,
    slideIdsToInclude: slideIdsToIncludeFilter,
  } = queryParams;

  const casesParams: CasesParams = {
    studyId: filters?.studyId,
    filters: {
      ...filters,
      ...(searchTerm ? { searchTerm } : {}),
      ...overrides?.filters,
    },
    features: {
      ...features,
      ...overrides?.features,
    },
    clinicalData: {
      ...clinicalData,
      ...overrides?.clinicalData,
    },
    labId,
    slidesMode: overrides?.slidesMode || slidesMode,
    resultsMode: overrides?.resultsMode || resultsMode || ResultsMode.Manual,
  };

  // Branch selection intentionally reads the raw query param, not the override applied above.
  if (!slidesMode) {
    const caseIdsToIncludeSelect = selectionMode === 'select' ? (selectedRows as number[]) : undefined;
    const caseIdsToExcludeSelect = selectionMode === 'omit' ? (omittedRows as number[]) : undefined;

    if (!isEmpty(caseIdsToIncludeFilter)) {
      let caseIdsToIncludeWithFilteredOptions;
      if (isEmpty(caseIdsToIncludeSelect) && isEmpty(caseIdsToExcludeSelect)) {
        caseIdsToIncludeWithFilteredOptions = map(caseIdsToIncludeFilter, Number);
      } else if (!isEmpty(caseIdsToExcludeSelect)) {
        caseIdsToIncludeWithFilteredOptions = difference(map(caseIdsToIncludeFilter, Number), caseIdsToExcludeSelect);
      } else {
        caseIdsToIncludeWithFilteredOptions = caseIdsToIncludeSelect;
      }

      casesParams.caseIdsToInclude = map(caseIdsToIncludeWithFilteredOptions, (caseId) => Number(caseId));
      if (slideIdsToIncludeFilter) {
        casesParams.slideIdsToInclude = slideIdsToIncludeFilter;
      }
    } else {
      casesParams.caseIdsToInclude = caseIdsToIncludeSelect;
      casesParams.caseIdsToExclude = caseIdsToExcludeSelect;
      if (slideIdsToIncludeFilter) {
        casesParams.slideIdsToInclude = slideIdsToIncludeFilter;
      }
    }

    return casesParams;
  }

  const slideIdsToInclude =
    selectionMode === 'select' ? getInnerRowIdFromUnwoundRowMetadata(selectedRows as string[]) : undefined;
  const slideIdsToExclude =
    selectionMode === 'omit' ? getInnerRowIdFromUnwoundRowMetadata(omittedRows as string[]) : undefined;

  if (!isEmpty(slideIdsToIncludeFilter)) {
    let slideIdsToIncludeWithFilteredOptions;
    if (isEmpty(slideIdsToInclude) && isEmpty(slideIdsToExclude)) {
      slideIdsToIncludeWithFilteredOptions = slideIdsToIncludeFilter;
    } else if (!isEmpty(slideIdsToExclude)) {
      slideIdsToIncludeWithFilteredOptions = difference(slideIdsToIncludeFilter, slideIdsToExclude);
    } else {
      slideIdsToIncludeWithFilteredOptions = intersection(slideIdsToIncludeFilter, slideIdsToInclude);
    }

    casesParams.slideIdsToInclude = slideIdsToIncludeWithFilteredOptions;
    if (caseIdsToIncludeFilter) {
      casesParams.caseIdsToInclude = map(caseIdsToIncludeFilter, Number);
    }
  } else {
    casesParams.slideIdsToInclude = slideIdsToInclude;
    casesParams.slideIdsToExclude = slideIdsToExclude;
    if (caseIdsToIncludeFilter) {
      casesParams.caseIdsToInclude = map(caseIdsToIncludeFilter, Number);
    }
  }

  return casesParams;
};
